import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Subject, catchError, of, takeUntil } from 'rxjs';
import {
  CustomerPrepaidPurchase,
  CustomerPrepaidRedemption,
  CustomerPrepaidService,
} from '../../services/customer-prepaid.service';
import { CancellationSettingsService } from '../../services/cancellation-settings.service';
import { LocationService } from '../../services/location.service';
import { ToastService } from '../../services/toast.service';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import { getUserDataLocationId } from '../../shared/utils/auth-headers.util';
import {
  canCancelPrepaid,
  prepaidCancelNotice,
  prepaidBookHasSeveralWashKinds,
  prepaidCardOnFileLabel,
  prepaidPurchaserCells,
  prepaidRedemptionLogTitle,
  prepaidRedemptionRows,
  PREPAID_REDEMPTION_LOG_HELP,
  PrepaidRedemptionRow,
  prepaidSourceLabel,
  prepaidStatusLabel,
  prepaidVehicleLabel,
  prepaidWashNamesBadge,
  prepaidWashNamesLabel,
  prepaidWashesLeftLabel,
  splitPrepaidPurchases,
} from '../../shared/utils/prepaid-purchase.util';

/** The log of one card: fetched when its section is first opened, then kept. */
interface RedemptionLogState {
  open: boolean;
  loading: boolean;
  error: string;
  rows: CustomerPrepaidRedemption[] | null;
}

/**
 * CWS-1833: Customers → Prepaid.
 *
 * One card per prepaid wash book the customer has bought, live ones first. Cancel
 * prepaid opens the shared cancel & refund panel (CWS-1778) keyed by the book's
 * sign-up order, so the options, execute and redemption-revocation paths
 * (CWS-1775) do the work. Add washes and Extend expiry from the design are not
 * built; the row menu offers Cancel prepaid only.
 *
 * CWS-1881 adds "Purchased by" and a collapsed "Redemption history" to each card. The
 * log is fetched the first time a card's section is opened — the design keeps it shut
 * and the card already knows the count — and each row that has a customer record offers
 * a link that asks the host to open that customer instead.
 */
@Component({
  selector: 'app-customer-prepaid-tab',
  standalone: true,
  imports: [CommonModule, CancelRefundPanelComponent],
  templateUrl: './customer-prepaid-tab.component.html',
  styleUrls: ['./customer-prepaid-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CustomerPrepaidTabComponent implements OnChanges, OnDestroy {
  @Input() customerId = '';
  /** For the cancel panel's subtitle: whose book is about to end. */
  @Input() customerName = '';
  /** Loaded by the parent so the tab can be hidden before it is ever opened. */
  @Input() purchases: CustomerPrepaidPurchase[] = [];

  /** Hands the refreshed list up so the parent re-evaluates the tab without refetching. */
  @Output() purchasesChanged = new EventEmitter<CustomerPrepaidPurchase[]>();
  /**
   * CWS-1881: a redemption row's "view customer". The tab cannot open a customer itself —
   * the drawer is owned by the customer table — so it asks up the chain by id.
   */
  @Output() openCustomer = new EventEmitter<string>();

  /** CWS-1881: redemption log per card, keyed by purchase id. */
  readonly logs = new Map<string, RedemptionLogState>();
  readonly logHelp = PREPAID_REDEMPTION_LOG_HELP;

  isLoading = false;
  loadError = '';

  /** Cancel & refund is on at this location. The API re-checks. */
  cancelAvailable = false;
  /** Book being cancelled; the panel opens over the cards while set. */
  cancelPurchaseId: string | null = null;
  /** What the last cancellation did, shown above the refreshed cards. */
  cancelNotice: { title: string; body: string } | null = null;
  /** The card whose row menu is open. */
  openMenuId: string | null = null;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroy$ = new Subject<void>();

  /**
   * Closes the row menu on a click or tap outside it. Capture phase: the customer details modal
   * stops click propagation on its container, so a bubbling `document:click` never arrives here.
   */
  private readonly onOutsideClick = (event: Event): void => {
    if (!this.openMenuId) {
      return;
    }
    const target = event.target as Node;
    const menus = Array.from(this.host.nativeElement.querySelectorAll('[data-prepaid-menu]'));
    if (!menus.some(menu => menu.contains(target))) {
      this.openMenuId = null;
    }
  };

  constructor(
    private readonly customerPrepaidService: CustomerPrepaidService,
    private readonly cancellationSettings: CancellationSettingsService,
    private readonly locationService: LocationService,
    private readonly toast: ToastService
  ) {
    const document = inject<Document>(DOCUMENT);
    document.addEventListener('click', this.onOutsideClick, true);
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('click', this.onOutsideClick, true)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId']) {
      this.closeCancel();
      this.openMenuId = null;
      this.cancelNotice = null;
      this.logs.clear();
      this.loadAvailability();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get locationId(): string | null {
    const id = String(
      this.locationService.getShopLocation()?.id ?? getUserDataLocationId() ?? ''
    ).trim();
    return id || null;
  }

  get livePurchases(): CustomerPrepaidPurchase[] {
    return splitPrepaidPurchases(this.purchases).live;
  }

  get pastPurchases(): CustomerPrepaidPurchase[] {
    return splitPrepaidPurchases(this.purchases).past;
  }

  /** The book the open cancel panel belongs to. */
  get cancelPurchase(): CustomerPrepaidPurchase | null {
    const id = this.cancelPurchaseId;
    return id ? (this.purchases.find(p => p.prepaid_purchase_id === id) ?? null) : null;
  }

  trackByPurchaseId(_index: number, purchase: CustomerPrepaidPurchase): string {
    return purchase.prepaid_purchase_id;
  }

  statusLabel(purchase: CustomerPrepaidPurchase): string {
    return prepaidStatusLabel(purchase.display_status);
  }

  plate(purchase: CustomerPrepaidPurchase): string {
    return (purchase.license_plate || '').trim();
  }

  vehicleLabel(purchase: CustomerPrepaidPurchase): string {
    return prepaidVehicleLabel(purchase);
  }

  hasPrice(purchase: CustomerPrepaidPurchase): boolean {
    return purchase.price !== null && purchase.price !== '';
  }

  washesLeftLabel(purchase: CustomerPrepaidPurchase): string {
    return prepaidWashesLeftLabel(purchase);
  }

  /** The full list, for the chip's tooltip. */
  washNamesLabel(purchase: CustomerPrepaidPurchase): string {
    return prepaidWashNamesLabel(purchase.washes);
  }

  /** The chip: first wash and an ellipsis when there are more. */
  washNamesBadge(purchase: CustomerPrepaidPurchase): string {
    return prepaidWashNamesBadge(purchase.washes);
  }

  cardOnFileLabel(purchase: CustomerPrepaidPurchase): string {
    return prepaidCardOnFileLabel(purchase.card_on_file);
  }

  sourceLabel(purchase: CustomerPrepaidPurchase): string {
    return prepaidSourceLabel(purchase);
  }

  /* -------------------------------- CWS-1881: purchaser + log -------------------------------- */

  purchaserCells(purchase: CustomerPrepaidPurchase) {
    return prepaidPurchaserCells(purchase.purchaser);
  }

  logTitle(purchase: CustomerPrepaidPurchase): string {
    return prepaidRedemptionLogTitle(purchase.redeemed_washes);
  }

  logState(purchase: CustomerPrepaidPurchase): RedemptionLogState {
    let state = this.logs.get(purchase.prepaid_purchase_id);
    if (!state) {
      state = { open: false, loading: false, error: '', rows: null };
      this.logs.set(purchase.prepaid_purchase_id, state);
    }
    return state;
  }

  logRows(purchase: CustomerPrepaidPurchase): PrepaidRedemptionRow[] {
    return prepaidRedemptionRows(this.logState(purchase).rows, {
      showWashName: prepaidBookHasSeveralWashKinds(purchase.washes),
    });
  }

  trackByRowId(_index: number, row: PrepaidRedemptionRow): string {
    return row.id;
  }

  /** Opens or closes a card's log; the first open fetches it, later opens reuse it. */
  toggleLog(purchase: CustomerPrepaidPurchase): void {
    const state = this.logState(purchase);
    state.open = !state.open;
    if (state.open && state.rows === null && !state.loading) {
      this.loadLog(purchase);
    }
  }

  retryLog(purchase: CustomerPrepaidPurchase): void {
    this.loadLog(purchase);
  }

  onViewCustomer(row: PrepaidRedemptionRow): void {
    if (row.customerId) {
      this.openCustomer.emit(row.customerId);
    }
  }

  private loadLog(purchase: CustomerPrepaidPurchase): void {
    const state = this.logState(purchase);
    state.loading = true;
    state.error = '';
    this.customerPrepaidService
      .getPrepaidRedemptions(purchase.prepaid_purchase_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: rows => {
          state.rows = rows;
          state.loading = false;
        },
        error: () => {
          state.loading = false;
          state.error = 'Could not load the redemption history.';
        },
      });
  }

  /** Cancel prepaid is the only row action; a card with none shows no menu, never a disabled item. */
  hasActions(purchase: CustomerPrepaidPurchase): boolean {
    return this.canCancel(purchase);
  }

  canCancel(purchase: CustomerPrepaidPurchase): boolean {
    return canCancelPrepaid(purchase, this.cancelAvailable);
  }

  toggleMenu(purchase: CustomerPrepaidPurchase): void {
    const id = purchase.prepaid_purchase_id;
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  @HostListener('document:keydown.escape')
  closeMenu(): void {
    this.openMenuId = null;
  }

  onCancelPrepaid(purchase: CustomerPrepaidPurchase): void {
    this.openMenuId = null;
    if (!this.canCancel(purchase)) {
      return;
    }
    this.cancelNotice = null;
    this.cancelPurchaseId = purchase.prepaid_purchase_id;
  }

  closeCancel(): void {
    this.cancelPurchaseId = null;
  }

  /** The cancellation ran: say what happened and refresh so the card moves to Past. */
  onCancelDone(done: CancelRefundDone): void {
    this.cancelNotice = prepaidCancelNotice(done);
    if (done.result.status !== 'failed') {
      this.toast.showSuccess(this.cancelNotice.title, this.cancelNotice.body);
    }
    this.cancelPurchaseId = null;
    this.reload();
  }

  reload(): void {
    const customerId = (this.customerId || '').trim();
    if (!customerId) {
      return;
    }
    this.isLoading = true;
    this.loadError = '';
    this.customerPrepaidService
      .getCustomerPrepaidPurchases(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: purchases => {
          this.purchases = purchases;
          this.isLoading = false;
          this.purchasesChanged.emit(purchases);
        },
        error: () => {
          this.isLoading = false;
          this.loadError = 'Could not refresh prepaid books. Close and reopen the customer.';
        },
      });
  }

  private loadAvailability(): void {
    const locationId = this.locationId;
    this.cancelAvailable = false;
    if (!locationId) {
      return;
    }
    // Cancel & refund is per location, as on the Subscriptions tab (CWS-1780).
    this.cancellationSettings
      .getAvailability(locationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe(response => {
        this.cancelAvailable = response?.data?.enabled === true;
      });
  }
}
